import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { config } from '../config';
import { ConversationsComponent } from '../conversations/conversations.component';
import { ContactsComponent } from './contacts/contacts.component';
import { ConversationComponent } from '../conversations/conversation/conversation.component';
import { CommunicationTemplatesComponent } from '../communication-templates/communication-templates.component';
import { StageManagementComponent } from '../stage-management/stage-management.component';
import { InterviewsComponent } from '../interviews/interviews.component';
import { AuthService, UserRole } from '../auth/auth.service';
import { ConversationService } from '../services/conversation.service';
import { Conversation } from '../models/conversation.model';
import { PermissionService } from '../shared/services/permission.service';
import { Router, ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, ConversationsComponent, ContactsComponent, ConversationComponent, CommunicationTemplatesComponent, StageManagementComponent, InterviewsComponent],
  template: `
    <div class="container">
      <div class="header">
     
      </div>
      
      <div class="navigation">
        <button 
          class="nav-button" 
          [class.active]="activeTab === 'conversations'"
          (click)="setActiveTab('conversations')">
          Conversations
        </button>
        <button 
          class="nav-button" 
          [class.active]="activeTab === 'contacts'"
          (click)="setActiveTab('contacts')">
          Contacts
        </button>
        <button 
          class="nav-button" 
          [class.active]="activeTab === 'interviews'"
          (click)="setActiveTab('interviews')">
          Interviews
        </button>
        <button 
          *ngIf="canManageStages()"
          class="nav-button" 
          [class.active]="activeTab === 'stages'"
          (click)="setActiveTab('stages')">
          Manage Stages
        </button>
        <button 
          *ngIf="canManageTemplates()"
          class="nav-button" 
          [class.active]="activeTab === 'templates'"
          (click)="setActiveTab('templates')">
          Templates
        </button>
      </div>
      
      <div class="card">
        <app-conversations *ngIf="activeTab === 'conversations'"></app-conversations>
        <app-contacts *ngIf="activeTab === 'contacts'"></app-contacts>
        <app-interviews *ngIf="activeTab === 'interviews'"></app-interviews>
        <app-stage-management *ngIf="activeTab === 'stages'"></app-stage-management>
        <app-communication-templates *ngIf="activeTab === 'templates'"></app-communication-templates>
      </div>
      
      <!-- Bottom nav (mobile only) -->
      <nav class="bottom-nav">
        <button class="bottom-nav-btn" [class.active]="activeTab === 'conversations'" (click)="setActiveTab('conversations')">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
          <span>Chats</span>
        </button>
        <button class="bottom-nav-btn" [class.active]="activeTab === 'contacts'" (click)="setActiveTab('contacts')">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
          <span>Contacts</span>
        </button>
        <button class="bottom-nav-btn" [class.active]="activeTab === 'interviews'" (click)="setActiveTab('interviews')">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>
          <span>Interviews</span>
        </button>
        <button *ngIf="canManageStages()" class="bottom-nav-btn" [class.active]="activeTab === 'stages'" (click)="setActiveTab('stages')">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>
          <span>Stages</span>
        </button>
        <button *ngIf="canManageTemplates()" class="bottom-nav-btn" [class.active]="activeTab === 'templates'" (click)="setActiveTab('templates')">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
          <span>Templates</span>
        </button>
      </nav>

      <!-- Global Conversation Modal -->
      <ng-container *ngIf="activeConversation">
        <app-conversation 
          [conversation]="activeConversation"
          (close)="handleCloseConversation()">
        </app-conversation>
      </ng-container>
    </div>
  `,
  styles: [`
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .header h1 {
      margin: 0;
      color: var(--ap-text, #0B1220);
      font-size: 1.15rem;
      font-weight: 650;
      letter-spacing: -0.02em;
      line-height: 1.2;
    }

    .navigation {
      display: flex;
      gap: 4px;
      margin-bottom: 20px;
      padding-bottom: 0;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
    }

    .navigation::-webkit-scrollbar { height: 0; }

    .nav-button {
      background-color: transparent;
      color: var(--ap-text-soft, #4B5563);
      border: none;
      border-bottom: 2px solid transparent;
      padding: 10px 16px;
      border-radius: 0;
      margin-bottom: -1px;
      cursor: pointer;
      font-size: 1.3em;
      font-weight: 500;
      line-height: 1.4;
      transition: color 0.15s, border-color 0.15s;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .nav-button:hover {
      color: var(--ap-text, #0B1220);
      background-color: transparent;
    }

    .nav-button.active {
      color: var(--ap-primary, #2F5BDA);
      border-bottom-color: var(--ap-primary, #2F5BDA);
      background-color: transparent;
    }

    .card {
      background-color: var(--ap-surface, #fff);
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-lg, 14px);
      box-shadow: var(--ap-shadow-sm);
      padding: 20px;
      margin-bottom: 20px;
      width: 100%;
      box-sizing: border-box;
    }

    .card:first-of-type {
      min-height: 400px;
    }

    /* Bottom nav — mobile only */
    .bottom-nav {
      display: none;
    }

    .bottom-nav-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 3px;
      flex: 1;
      background: none;
      border: none;
      height: auto;
      margin: 0;
      padding: 8px 4px;
      color: var(--ap-text-muted, #6B7280);
      font-size: 10px;
      line-height: 1.2;
      cursor: pointer;
      border-radius: 0;
      width: auto;
      transition: color 0.15s;
    }

    .bottom-nav-btn svg {
      width: 22px;
      height: 22px;
      flex-shrink: 0;
    }

    .bottom-nav-btn.active,
    .bottom-nav-btn.active:hover {
      color: var(--ap-primary, #2F5BDA);
      background: none;
    }

    .bottom-nav-btn:hover {
      background: none;
      color: var(--ap-text-soft, #4B5563);
    }

    /* Tablet */
    @media (max-width: 1024px) {
      .container { max-width: 100%; padding: 16px; }
      .nav-button { padding: 8px 14px; }
      .card { padding: 16px; }
    }

    /* Mobile */
    @media (max-width: 768px) {
      .container { padding: 12px 12px 0 12px; }
      .navigation { display: none; }

      .bottom-nav {
        display: flex;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
        background: var(--ap-surface, #fff);
        border-top: 1px solid var(--ap-border, #E3E6EB);
        box-shadow: 0 -1px 0 var(--ap-border, #E3E6EB), 0 -4px 12px rgba(13,18,30,.06);
        padding: 4px 0 env(safe-area-inset-bottom, 4px);
      }

      .bottom-nav-btn { width: 100%; margin-bottom: 0; }

      .header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 16px;
      }

      .header h1 { font-size: 1.1rem; width: 100%; }

      .card {
        padding: 16px;
        margin-bottom: 72px;
        border-radius: var(--ap-radius, 10px);
      }

      .card:first-of-type { min-height: 300px; }
    }

    /* Small Mobile */
    @media (max-width: 480px) {
      .container { padding: 8px; }
      .card { padding: 12px; margin-bottom: 12px; }
      .card:first-of-type { min-height: 250px; }
    }

    /* Extra Small */
    @media (max-width: 320px) {
      .container { padding: 6px; }
      .header h1 { font-size: 1rem; }
      .card { padding: 10px; }
    }

    /* Large Desktop */
    @media (min-width: 1200px) {
      .container { padding: 24px; }
      .header h1 { font-size: 1.25rem; }
      .nav-button { padding: 10px 20px; font-size: 16px; }
      .card { padding: 24px; margin-bottom: 24px; }
    }

    :host ::ng-deep app-conversations,
    :host ::ng-deep app-contacts,
    :host ::ng-deep app-communication-templates {
      width: 100%;
      display: block;
    }
  `]
})
export class DashboardComponent implements OnInit, OnDestroy {
  private apiUrl = config.apiUrl;
  public activeTab: string = 'conversations';
  public activeConversation: Conversation | null = null;
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    public authService: AuthService,
    private conversationService: ConversationService,
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Block God users from accessing the regular dashboard
    const currentUser = this.authService.currentUserValue;
    if (currentUser?.role === UserRole.PLATFORM_OWNER) {
      console.log('God user detected, redirecting to God dashboard');
      this.router.navigate(['/god/overview']);
      return;
    }
    
    // Restore active tab from URL query param
    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    const validTabs = ['conversations', 'contacts', 'interviews', 'stages', 'templates'];
    if (tabParam && validTabs.includes(tabParam)) {
      this.activeTab = tabParam;
    }

    // Subscribe to active conversation changes for global modal display
    this.subscriptions.add(
      this.conversationService.activeConversation$.subscribe(conversation => {
        this.activeConversation = conversation;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  handleCloseConversation(): void {
    this.conversationService.setActiveConversation(null);
  }

  canManageTemplates(): boolean {
    const currentUser = this.authService.currentUserValue;
    return this.permissionService.canManageTemplates(currentUser?.role);
  }

  canManageStages(): boolean {
    const currentUser = this.authService.currentUserValue;
    return this.permissionService.canManageStages(currentUser?.role);
  }

  // Company isolation checks - ensure users only see their own company's data
  getCompanyFilteredData(): void {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser?.company_id) {
      console.warn('Dashboard: User has no company association');
      return;
    }
    
    // All data loading in child components should be filtered by company_id
    // This is enforced by the backend services and permission middleware
  }
}
